import { useQuery } from '@tanstack/react-query';
import type {
  ActivityEntry,
  AgingBucket,
  DashboardSummary,
  InvoiceFlagBucket,
  InvoiceStatusBucket,
  MonthlyCostPoint,
  ProjectCostSummary,
} from '@faktorplus/shared';
import { apiRequest } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

export function useDashboardSummary() {
  return useQuery({
    queryKey: queryKeys.reports.dashboard,
    queryFn: () => apiRequest<DashboardSummary>('/reports/dashboard'),
  });
}

export function useActivityFeed() {
  return useQuery({
    queryKey: queryKeys.reports.activity,
    queryFn: () => apiRequest<ActivityEntry[]>('/reports/activity'),
  });
}

export function useCostsByProject(period: { from?: string; to?: string } = {}) {
  const query = {
    ...(period.from ? { from: period.from } : {}),
    ...(period.to ? { to: period.to } : {}),
  };

  return useQuery({
    queryKey: queryKeys.reports.costsByProject(query),
    queryFn: () =>
      apiRequest<ProjectCostSummary[]>('/reports/costs/by-project', { query }),
  });
}

export function useCostsByMonth() {
  return useQuery({
    queryKey: ['reports', 'costs-by-month'] as const,
    queryFn: () => apiRequest<MonthlyCostPoint[]>('/reports/costs/by-month'),
  });
}

export function useInvoicesByStatus(period: { from?: string; to?: string } = {}) {
  const query = {
    ...(period.from ? { from: period.from } : {}),
    ...(period.to ? { to: period.to } : {}),
  };

  return useQuery({
    queryKey: ['reports', 'invoices-by-status', query] as const,
    queryFn: () => apiRequest<InvoiceStatusBucket[]>('/reports/invoices/by-status', { query }),
  });
}

export function useInvoiceFlags(period: { from?: string; to?: string } = {}) {
  const query = {
    ...(period.from ? { from: period.from } : {}),
    ...(period.to ? { to: period.to } : {}),
  };

  return useQuery({
    queryKey: ['reports', 'invoice-flags', query] as const,
    queryFn: () => apiRequest<InvoiceFlagBucket[]>('/reports/invoices/flags', { query }),
  });
}

export function usePayablesAging() {
  return useQuery({
    queryKey: ['reports', 'payables-aging'] as const,
    queryFn: () => apiRequest<AgingBucket[]>('/reports/payables/aging'),
  });
}
